import React, { useState, useCallback, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import ApplicantForm from "../Forms/ApplicantForm";
import ContractorForm from "../Forms/Contractor";
import LocationForm from "../Forms/Location";
import { validateField } from "../../utils/validation/validation";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { Row, Button } from "react-bootstrap";
import ListGroup from "react-bootstrap/ListGroup";
import InputField from "../../utils/InputField";
import TextAreaField from "../../utils/TextAreaField";
import { updateApplication, updatePortalApplication, fetchPortalApplicationById } from "../../services/api";
import { useToast } from "../../contexts/ToastContext";
import { useLoading } from "../../contexts/LoadingContext";
import Dropzone from "../../utils/dropzone";
import { useAuth } from "../../contexts/AuthContext";
import Alert from "react-bootstrap/Alert";

function ApplicationModal({ show, onHide, data }) {
  const { role } = useAuth();
  const { showToast } = useToast();
  const { startLoading, stopLoading } = useLoading();
  
  // Helper function to format number with commas for display
  const formatCostForDisplay = (cost) => {
    if (!cost && cost !== 0) return "";
    const numericCost = String(cost).replace(/,/g, "");
    const parts = numericCost.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join('.');
  };
  
  // Helper function to format phone number as XXX XXX XXXX
  const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    // Remove all non-numeric characters
    const digitsOnly = String(phone).replace(/\D/g, "");
    // Limit to 10 digits (US phone number)
    const limitedNumeric = digitsOnly.substring(0, 10);
    // Format as XXX XXX XXXX
    if (limitedNumeric.length > 6) {
      return `${limitedNumeric.substring(0, 3)} ${limitedNumeric.substring(3, 6)} ${limitedNumeric.substring(6)}`;
    } else if (limitedNumeric.length > 3) {
      return `${limitedNumeric.substring(0, 3)} ${limitedNumeric.substring(3)}`;
    } else {
      return limitedNumeric;
    }
  };
  
  // Format contractors' phone numbers when loading
  const formatContractors = (contractors) => {
    if (!contractors || !Array.isArray(contractors)) return [];
    return contractors.map(contractor => ({
      ...contractor,
      villageEmail: undefined,
      email: contractor?.email || defaultVillageEmail,
      contractorEmail: contractor?.contractorEmail || "",
      phoneNumber: formatPhoneNumber(contractor.phoneNumber || "")
    }));
  };
  
  // Extract file IDs from location files (which come as objects with link properties)
  const extractLocationFileIds = (files) => {
    if (!files || !Array.isArray(files) || files.length === 0) return [];
    return files
      .map(file => {
        // Extract file ID from link like "http://.../attachments/files/123"
        if (file.link) {
          const match = file.link.match(/\/files\/(\d+)/);
          return match ? parseInt(match[1], 10) : null;
        }
        // If it's already a number, return it
        if (typeof file === 'number') return file;
        return null;
      })
      .filter(id => id !== null);
  };
  
  const initialFormValue = {
    applicant: {
      firstName: data?.firstName || "",
      lastName: data?.lastName || "",
      address: data?.address || "",
      email: data?.email || "",
      phoneNumber: formatPhoneNumber(data?.phoneNumber || ""),
      notes: data?.notes || "",
      role: data?.role || "",
    },
    contractors: formatContractors(data?.contractors || []),
    location: {
      address: data?.location?.address || "",
      permitType: data?.location?.permitType || "New Construction",
      description: data?.location?.description || "",
      cost: formatCostForDisplay(data?.location?.cost) || "",
      locationFiles: extractLocationFileIds(data?.location?.files || []),
    },
    app: {
      status: data?.status || "Pending",
      permitFee: formatCostForDisplay(data?.permitFee) || "",
      planReviewFee: formatCostForDisplay(data?.planReviewFee) || "",
      inspectionsFee: formatCostForDisplay(data?.inspectionsFee) || "",
      adminComment: data?.adminComment || data?.reason || "",
      permitNumber: data?.permitNumber || "",
      standardReport: data?.standardReport || [],
      files: data?.location?.files || [],
    },
  };

  const defaultVillageEmail = "commercialpermits@pingreegrove.org";

  const intialContractorFields = {
    individualName: "",
    trade: "",
    email: defaultVillageEmail,
    contractorEmail: "",
    phoneNumber: "",
    address: "",
    license: [],
    licenseLink: [],
  };
  const [formData, setFormData] = useState(initialFormValue);
  const [selectedContractorIndex, setSelectedContractorIndex] = useState(null);
  const [addOrUpdateButtonText, setAddOrUpdateButtonText] =
    useState("Add Contractor");
  const [currentContractor, setCurrentContractor] = useState(
    intialContractorFields
  );
  const [errors, setErrors] = useState({
    applicant: {},
    contractors: [],
    location: {},
    community: null,
  });
  const [editLock, setEditLock] = useState({ editable: true, reason: "" });
  
  // Update form data when modal opens with new data
  useEffect(() => {
    if (show && data) {
      const initFormData = (appData) => {
        setFormData({
          applicant: {
            firstName: appData?.firstName || "",
            lastName: appData?.lastName || "",
            address: appData?.address || "",
            email: appData?.email || "",
            phoneNumber: formatPhoneNumber(appData?.phoneNumber || ""),
            notes: appData?.notes || "",
            role: appData?.role || "",
          },
          contractors: formatContractors(appData?.contractors || []),
          location: {
            address: appData?.location?.address || "",
            permitType: appData?.location?.permitType || "New Construction",
            description: appData?.location?.description || "",
            cost: formatCostForDisplay(appData?.location?.cost) || "",
            locationFiles: extractLocationFileIds(appData?.location?.files || []),
          },
          app: {
            status: appData?.status || "Pending",
            permitFee: formatCostForDisplay(appData?.permitFee) || "",
            planReviewFee: formatCostForDisplay(appData?.planReviewFee) || "",
            inspectionsFee: formatCostForDisplay(appData?.inspectionsFee) || "",
            adminComment: appData?.adminComment || appData?.reason || "",
            permitNumber: appData?.permitNumber || "",
            standardReport: appData?.standardReport || [],
            files: appData?.location?.files || [],
          },
        });
      };

      initFormData(data);
      setCurrentContractor(intialContractorFields);
      setSelectedContractorIndex(null);
      setAddOrUpdateButtonText("Add Contractor");

      if (role === "Applicant") {
        fetchPortalApplicationById(data.applicantId)
          .then((res) => {
            if (res.success) {
              if (res.application) {
                initFormData(res.application);
              }
              if (res.editLock) {
                setEditLock(res.editLock);
              }
            }
          })
          .catch((err) => {
            console.error("Failed to fetch application details or lock status:", err);
          });
      } else {
        setEditLock({ editable: true, reason: "" });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, data, role]);
  const validateContractorFields = () => {
    const errors = {};
    const requiredFields = [
      "individualName",
      "trade",
      "email",
      "contractorEmail",
      "phoneNumber",
      "address",
    ];

    requiredFields.forEach((field) => {
      // Skip address validation in modal mode to allow address to be visible
      if (field === 'address') {
        // Don't validate address - allow it to be optional in modal
        return;
      }
      const error = validateField(field, currentContractor[field]);
      if (error) {
        errors[field] = error;
      }
    });

    setErrors((prevErrors) => ({
      ...prevErrors,
      currentContractor: errors,
    }));

    return Object.keys(errors).length === 0;
  };
  const handleAddOrUpdateContractor = () => {
    if (validateContractorFields()) {
      setFormData((prevData) => {
        const updatedContractors = [...prevData.contractors];
        // Ensure license is always an array
        const { villageEmail, ...restContractor } = currentContractor || {};
        const contractorToSave = {
          ...restContractor,
          email: restContractor?.email || villageEmail || defaultVillageEmail,
          contractorEmail: restContractor?.contractorEmail || "",
          license: Array.isArray(currentContractor.license) ? currentContractor.license : []
        };
        if (selectedContractorIndex !== null) {
          updatedContractors[selectedContractorIndex] = contractorToSave;
        } else {
          updatedContractors.push(contractorToSave);
        }
        return { ...prevData, contractors: updatedContractors };
      });
      setCurrentContractor(intialContractorFields);
      setSelectedContractorIndex(null);
      setAddOrUpdateButtonText("Add Contractor");
    }
  };
  const handleSelectContractor = (index) => {
    const contractor = formData.contractors[index];
    // Ensure license is always an array
    const contractorToSet = {
      ...contractor,
      villageEmail: undefined,
      email: contractor?.email || defaultVillageEmail,
      contractorEmail: contractor?.contractorEmail || "",
      phoneNumber: formatPhoneNumber(contractor.phoneNumber || ""),
      license: Array.isArray(contractor.license) ? contractor.license : (contractor.license ? [contractor.license] : [])
    };
    setCurrentContractor(contractorToSet);
    setSelectedContractorIndex(index);
    setAddOrUpdateButtonText("Update Contractor");
    // Clear address errors when selecting contractor in modal
    setErrors((prevErrors) => ({
      ...prevErrors,
      currentContractor: { ...prevErrors.currentContractor, address: null }
    }));
  };
  const handleDataChange = useCallback((section, field, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [section]: { ...prevData[section], [field]: value },
    }));
  }, []);
  const handleValidate = useCallback((section, field, error) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [section]: { ...prevErrors[section], [field]: error },
    }));
  }, []);

  const validateEntireForm = () => {
    const applicantErrors = {};
    const locationErrors = {};
    const contractorErrors = formData.contractors.map(() => ({}));

    let isValid = true;

    // Skip validation for notes field in edit modal
    Object.entries(formData.applicant).forEach(([field, value]) => {
      if (field === 'notes') return; // Skip notes validation
      const error = validateField(field, value);
      if (error) {
        applicantErrors[field] = error;
        isValid = false;
      }
    });

    Object.entries(formData.location).forEach(([field, value]) => {
      const error = validateField(field, value);
      if (error) {
        locationErrors[field] = error;
        isValid = false;
      }
    });

    setErrors({
      applicant: applicantErrors,
      location: locationErrors,
      contractors: contractorErrors,
    });
    return isValid;
  };

  const handleFileUpload = useCallback((fieldName, uploadedFiles) => {
          console.log('Files uploaded to field:', fieldName, uploadedFiles);
          handleDataChange('app',fieldName, uploadedFiles); 
  }, [handleDataChange]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = validateEntireForm();
    if (!isValid) return;
    if (addOrUpdateButtonText === "Update Contractor") {
      showToast("Please update contractor to save data", "error");
      return;
    }
    if (role === "Applicant" && !editLock.editable) {
      showToast("This application is locked and cannot be edited.", "error");
      return;
    }
    try {
      startLoading();
      console.log("Submitting form data:", formData.app.standardReport);
      if (formData.app.standardReport && formData.app.standardReport?.length === 0) {
        delete formData.app.standardReport;
      }
      // Clean cost and fee data: remove commas and convert to number for backend
      const cleanedFormData = {
        ...formData,
        location: {
          ...formData.location,
          cost: formData.location.cost ? Number(String(formData.location.cost).replace(/,/g, '')) : formData.location.cost
        },
        app: {
          ...formData.app,
          permitFee: formData.app.permitFee ? Number(String(formData.app.permitFee).replace(/,/g, '')) : formData.app.permitFee,
          planReviewFee: formData.app.planReviewFee ? Number(String(formData.app.planReviewFee).replace(/,/g, '')) : formData.app.planReviewFee,
          inspectionsFee: formData.app.inspectionsFee ? Number(String(formData.app.inspectionsFee).replace(/,/g, '')) : formData.app.inspectionsFee
        }
      };
      
      if (role === "Applicant") {
        await updatePortalApplication(data.applicantId, cleanedFormData);
      } else {
        await updateApplication(data.applicantId, cleanedFormData);
      }
      showToast("Update successfully!", "success");
      onHide();
    } catch (error) {
      showToast(error.message || "Update failed. Please try again.", "error");
    } finally {
      stopLoading();
    }
  };

  const isReadOnly = false;
  const applicationStatus = data ? data.status : "Pending";

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      aria-labelledby="add-community-modal"
      className="add-community-modal"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title className="text-uppercase" id="add-community-modal">
          Edit Application
        </Modal.Title>
        {data && (
          <div
            className={`modal-header-status status-${applicationStatus.toLowerCase()}`}
          >
            Status:{" "}
            <span className={`span-${applicationStatus.toLowerCase()}`}>
              {applicationStatus}
            </span>
          </div>
        )}
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <div className="form-wrapper mb-5 form-modal">
            <Row>
              {data && (
                role === "Applicant" ? (
                  <Col md="4" className="border-end pe-3">
                    <div className="mb-4">
                      <h5 className="text-uppercase fw-bold mb-3" style={{ fontSize: "0.95rem", color: "#555" }}>
                        Application Details
                      </h5>
                      
                      <div className="mb-3">
                        <label className="text-muted d-block small mb-1">Permit Number</label>
                        <div className="fw-semibold">{formData.app.permitNumber || "Pending"}</div>
                      </div>

                      <div className="mb-3">
                        <label className="text-muted d-block small mb-1">Permit Fee</label>
                        <div className="fw-semibold">
                          {formData.app.permitFee ? `$${formData.app.permitFee}` : "—"}
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="text-muted d-block small mb-1">Plan Review Fee</label>
                        <div className="fw-semibold">
                          {formData.app.planReviewFee ? `$${formData.app.planReviewFee}` : "—"}
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="text-muted d-block small mb-1">Inspections Fee</label>
                        <div className="fw-semibold">
                          {formData.app.inspectionsFee ? `$${formData.app.inspectionsFee}` : "—"}
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="text-muted d-block small mb-1">Administration Note</label>
                        <div className="text-muted" style={{ fontSize: "0.9rem", whiteSpace: "pre-wrap" }}>
                          {formData.app.adminComment || "No notes."}
                        </div>
                      </div>

                      <Row className="mb-3">
                        <InputField
                          field="permitType"
                          label="Commercial Permit Type"
                          value={formData.location.permitType}
                          onChange={(field, value) =>
                            handleDataChange("location", field, value)
                          }
                          error={errors.permitType}
                          type="select"
                          options={["New Construction", "Exterior Remodel", "Interior Remodel", "Building Work Only (Non-Structural)", "Building Work Only", "Electric Only", "Mechanical Only", "Plumbing Only","Sign Only"]}
                          required={false}
                          as={Col}
                          controlId="permitType"
                          disabled={isReadOnly}
                        />
                      </Row>
                    </div>

                    {formData.app?.standardReport?.length > 0 && (
                      <div className="mt-4">
                        <h6 className="text-uppercase fw-bold mb-2" style={{ fontSize: "0.85rem", color: "#555" }}>
                          Permit Reports
                        </h6>
                        <ListGroup as="ul" className="small">
                          {formData.app.standardReport.map((file, index) => (
                            <ListGroup.Item as="li" key={index} className="py-2">
                              <a href={file.link} target="_blank" rel="noopener noreferrer" className="text-success fw-semibold">
                                Download Report {index + 1}
                              </a>
                            </ListGroup.Item>
                          ))}
                        </ListGroup>
                      </div>
                    )}
                  </Col>
                ) : (
                  <Col md="4">
                    <Row className="mb-3">
                      <InputField
                        field="status"
                        label="Status"
                        value={formData.app.status}
                        onChange={(field, value) => handleDataChange("app", field, value)}
                        error={errors.status}
                        type="select"
                        options={["Submitted", "Pending", "Review Fee Due", "Review Fee Paid", "Village Fees Due", "Village Fees Paid", "Approved", "Finalized", "Void"]}
                        as={Col}
                        controlId="applicantStatus"
                      />
                    </Row>
                    <Row className="mb-3">
                      <InputField
                        field="permitNumber"
                        label="Permit Number"
                        value={formData.app.permitNumber}
                        onChange={(field, value) =>
                          handleDataChange("app", field, value)
                        }
                        required={false}
                        as={Col}
                      />
                    </Row>

                    <Row className="mb-3">
                      <InputField
                        field="permitFee"
                        label="Permit Fee"
                        value={formData.app.permitFee}
                        onChange={(field, value) =>
                          handleDataChange("app", field, value)
                        }
                        required={false}
                        showDollarSign={true}
                        placeholder=""
                        as={Col}
                      />
                    </Row>
                    <Row className="mb-3">
                      <InputField
                        field="planReviewFee"
                        label="Plan Review Fee"
                        value={formData.app.planReviewFee}
                        onChange={(field, value) =>
                          handleDataChange("app", field, value)
                        }
                        required={false}
                        showDollarSign={true}
                        placeholder=""
                        as={Col}
                      />
                    </Row>
                    <Row className="mb-3">
                      <InputField
                        field="inspectionsFee"
                        label="Inspections Fee"
                        value={formData.app.inspectionsFee}
                        onChange={(field, value) =>
                          handleDataChange("app", field, value)
                        }
                        required={false}
                        showDollarSign={true}
                        placeholder=""
                        as={Col}
                      />
                    </Row>
                    <Row className="mb-3">
                      <TextAreaField
                        field="adminComment"
                        label="Administration "
                        value={formData.app.adminComment}
                        onChange={(field, value) =>
                          handleDataChange("app", field, value)
                        }
                        error={null}
                        placeholder={""}
                        rows={3}
                        controlId="app"
                      />
                    </Row>
                    <Row className="mb-3">
                      <InputField
                        field="permitType"
                        label="Commercial Permit Type"
                        value={formData.location.permitType}
                        onChange={(field, value) =>
                          handleDataChange("location", field, value)
                        }
                        error={errors.permitType}
                        type="select"
                        options={["New Construction", "Exterior Remodel", "Interior Remodel", "Building Work Only (Non-Structural)", "Building Work Only", "Electric Only", "Mechanical Only", "Plumbing Only","Sign Only"]}
                        required={false}
                        as={Col}
                        controlId="permitType"
                      />
                    </Row>
                    <Row className="mb-3">
                        <div className="form-heading mt-5 text-uppercase fw-bold">
                              Permit Reports (Only PDF)
                          </div>
                          <Dropzone fileTypes={['application/pdf']} showPreview={true} fieldName="standardReport" onFileUpload={handleFileUpload} />
                          {(formData.app?.standardReport?.length > 0) && (
                              <Row>
                                  <Col sm="12">
                                      <div className="form-wrapper rounded mt-2">
                                          <div className="form-heading text-uppercase fw-bold">
                                              Download/View Permit Reports
                                          </div>
                                          <ListGroup as="ul">
                                              {formData.app.standardReport.map((file, index) => (
                                                  <ListGroup.Item as="li" key={index}>
                                                      <a href={file.link} target="_blank" rel="noopener noreferrer">
                                                          File {index + 1}
                                                      </a>
                                                  </ListGroup.Item>
                                              ))}
                                          </ListGroup>
                                      </div>
                                  </Col>
                              </Row>
                          )}
                    </Row>
                  </Col>
                )
              )}
              <Col md="8">
                <Tabs
                  defaultActiveKey="applicant"
                  id="uncontrolled-tab-example"
                  className="mb-3"
                >
                  <Tab eventKey="applicant" title="Applicant">
                    <ApplicantForm
                      data={formData.applicant}
                      onChange={(field, value) =>
                        handleDataChange("applicant", field, value)
                      }
                      onValidate={(field, error) =>
                        handleValidate("applicant", field, error)
                      }
                      errors={errors.applicant || {}}
                      isFrom={"modal"}
                      disabled={isReadOnly}
                    />
                  </Tab>
                  <Tab eventKey="contractor" title="Contractor">
                    <ContractorForm
                      data={currentContractor}
                      contractors={formData.contractors}
                      onChange={(field, value) =>
                        setCurrentContractor({
                          ...currentContractor,
                          [field]: value,
                        })
                      }
                      onValidate={(field, error) =>
                        handleValidate("currentContractor", field, error)
                      }
                      onAddOrUpdateContractor={handleAddOrUpdateContractor}
                      onSelectContractor={handleSelectContractor}
                      errors={errors.currentContractor || {}}
                      buttonText={addOrUpdateButtonText}
                      isFrom={"modal"}
                      disabled={isReadOnly}
                    />
                  </Tab>
                  <Tab eventKey="location" title="Location">
                    <LocationForm
                      data={formData.location}
                      onChange={(field, value) =>
                        handleDataChange("location", field, value)
                      }
                      onValidate={(field, error) =>
                        handleValidate("location", field, error)
                      }
                      errors={errors.location || {}}
                      isFrom={"modal"}
                      disabled={isReadOnly}
                    />
                    <Row>
                      <Col sm="12">
                        <div className="form-wrapper rounded mt-2">
                          <div className="form-heading text-uppercase fw-bold">
                            Download/View Files
                          </div>
                          {formData.app?.files && formData.app.files.length > 0 ? (
                            <ListGroup as="ul">
                              {formData.app.files.map((file, index) => (
                                <ListGroup.Item as="li" key={index}>
                                  <a
                                    href={file.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    File {index + 1}
                                  </a>
                                </ListGroup.Item>
                              ))}
                            </ListGroup>
                          ) : (
                            <p className="text-muted">No files available.</p>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </Tab>
                </Tabs>
              </Col>
            </Row>
          </div>
          {!isReadOnly && (
            <div className="text-end">
              <Button type="submit" className="btn btn-submit">
                Save Changes
              </Button>
            </div>
          )}
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default ApplicationModal;
